import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { 
  AlertTriangle,
  CheckCircle,
  Loader2,
  RefreshCw,
  XCircle,
  Search,
  HardDrive,
  Tag,
  Plus,
  Trash2,
  Edit,
  Cpu,
  Camera
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

function ReleaseVersionsManager() {
  const { isAdmin } = useAuth();
  // Release versions state
  const [releaseVersions, setReleaseVersions] = useState([]);
  const [releaseVersionsLoading, setReleaseVersionsLoading] = useState(false);
  const [releaseVersionsError, setReleaseVersionsError] = useState(null);
  const [editingRelease, setEditingRelease] = useState(null);
  const [newRelease, setNewRelease] = useState({
    lta_version: '',
    m1n_upgrade_file: null,
    dashcam_upgrade_file: null,
    notes: ''
  });
  const [showNewReleaseForm, setShowNewReleaseForm] = useState(false);
  const [savingRelease, setSavingRelease] = useState(false);
  
  // Release file search state
  const [releaseFileSearch, setReleaseFileSearch] = useState('');
  const [releaseFilesLoading, setReleaseFilesLoading] = useState(false);
  const [releaseFilesResult, setReleaseFilesResult] = useState([]);
  const [releaseFilesError, setReleaseFilesError] = useState(null);

  // Load release versions on mount
  useEffect(() => {
    loadReleaseVersions();
  }, []);

  const loadReleaseVersions = async () => {
    setReleaseVersionsLoading(true);
    setReleaseVersionsError(null);
    try {
      const response = await axios.get('/api/release-versions');
      setReleaseVersions(response.data.versions || []);
    } catch (err) {
      setReleaseVersionsError(err.response?.data?.error || 'Failed to load release versions');
    } finally {
      setReleaseVersionsLoading(false);
    }
  };

  // Search upgrade files for release
  const handleSearchReleaseFiles = async () => {
    setReleaseFilesLoading(true);
    setReleaseFilesError(null);

    try {
      const params = new URLSearchParams();
      if (releaseFileSearch) params.append('file_name', releaseFileSearch);
      params.append('page_size', '100');

      const response = await axios.get(`/api/ftcloud/upgrade-files?${params.toString()}`);
      
      if (response.data.success) {
        const files = response.data.data?.data || [];
        // Deduplicate by fileId or fileName
        const uniqueFiles = files.filter((file, index, self) => 
          index === self.findIndex(f => 
            (f.fileId && f.fileId === file.fileId) || 
            (f.fileName && f.fileName === file.fileName)
          )
        );
        setReleaseFilesResult(uniqueFiles);
      } else {
        setReleaseFilesError(response.data.error || 'Failed to fetch files');
      }
    } catch (err) {
      setReleaseFilesError(err.response?.data?.error || 'Failed to fetch upgrade files');
    } finally {
      setReleaseFilesLoading(false);
    }
  };

  const handleCreateRelease = async () => {
    if (!newRelease.lta_version.trim()) {
      setReleaseVersionsError('LTA Version is required');
      return;
    }

    setSavingRelease(true);
    setReleaseVersionsError(null);

    try {
      const releaseData = {
        name: newRelease.lta_version,
        lta_version: newRelease.lta_version,
        m1n_upgrade_file: newRelease.m1n_upgrade_file,
        dashcam_upgrade_file: newRelease.dashcam_upgrade_file,
        notes: newRelease.notes
      };
      
      const response = await axios.post('/api/release-versions', releaseData);
      if (response.data.success) {
        setReleaseVersions(prev => [...prev, response.data.version]);
        setNewRelease({
          lta_version: '',
          m1n_upgrade_file: null,
          dashcam_upgrade_file: null,
          notes: ''
        });
        setReleaseFilesResult([]);
        setReleaseFileSearch('');
        setShowNewReleaseForm(false);
      }
    } catch (err) {
      setReleaseVersionsError(err.response?.data?.error || 'Failed to create release');
    } finally {
      setSavingRelease(false);
    }
  };

  const handleSetCurrentRelease = async (name) => {
    setSavingRelease(true);
    setReleaseVersionsError(null);

    try {
      const response = await axios.post(`/api/release-versions/${encodeURIComponent(name)}/set-current`);
      if (response.data.success) {
        // Reload all releases to get updated is_current flags
        await loadReleaseVersions();
      }
    } catch (err) {
      setReleaseVersionsError(err.response?.data?.error || 'Failed to set current release');
    } finally {
      setSavingRelease(false);
    }
  };

  const handleDeleteRelease = async (name) => {
    if (!window.confirm(`Are you sure you want to delete release "${name}"?`)) {
      return;
    }

    try {
      const response = await axios.delete(`/api/release-versions/${encodeURIComponent(name)}`);
      if (response.data.success) {
        setReleaseVersions(prev => prev.filter(v => v.name !== name));
      }
    } catch (err) {
      setReleaseVersionsError(err.response?.data?.error || 'Failed to delete release');
    }
  };

  // Helper to get file type from filename
  const getFileType = (fileName) => {
    if (!fileName) return 'unknown';
    const lower = fileName.toLowerCase();
    if (lower.includes('m1n') || lower.includes('007')) return 'M1N';
    if (lower.includes('dashcam') || lower.includes('00d')) return 'Dashcam';
    return 'unknown';
  };

  return (
    <div className="space-y-4">
      <Card className="border-2 border-teal-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Tag className="w-6 h-6 text-teal-600" />
            Release Versions
          </CardTitle>
          <CardDescription>
            Manage release versions with LTA version and upgrade files for M1N and Dashcam devices
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Error Message */}
          {releaseVersionsError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5" />
              <p className="text-sm text-red-600">{releaseVersionsError}</p>
            </div>
          )}

          {/* Add New Release Button */}
          <div className="flex justify-between items-center">
            {isAdmin() && (
              <Button
                onClick={() => setShowNewReleaseForm(!showNewReleaseForm)}
                variant={showNewReleaseForm ? "outline" : "default"}
                className={showNewReleaseForm ? "" : "bg-teal-600 hover:bg-teal-700"}
              >
              {showNewReleaseForm ? (
                <>
                  <XCircle className="w-4 h-4 mr-2" />
                  Cancel
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  New Release
                </>
              )}
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={loadReleaseVersions}
              disabled={releaseVersionsLoading}
            >
              {releaseVersionsLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              <span className="ml-1">Refresh</span>
            </Button>
          </div>

          {/* New Release Form */}
          {showNewReleaseForm && (
            <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg space-y-4">
              <h4 className="font-medium text-teal-800">Create New Release</h4>
              
              {/* LTA Version */}
              <div className="space-y-2">
                <Label>LTA Version *</Label>
                <Input
                  placeholder="e.g., 1.2.3"
                  value={newRelease.lta_version}
                  onChange={(e) => setNewRelease(prev => ({ ...prev, lta_version: e.target.value }))}
                />
                <p className="text-xs text-gray-500">This will be used as the release identifier</p>
              </div>

              {/* Search Upgrade Files */}
              <div className="space-y-3 p-3 bg-white rounded border">
                <Label className="flex items-center gap-2">
                  <Search className="w-4 h-4" />
                  Search Upgrade Files
                </Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Filter by file name..."
                    value={releaseFileSearch}
                    onChange={(e) => setReleaseFileSearch(e.target.value)}
                    className="flex-1"
                  />
                  <Button
                    onClick={handleSearchReleaseFiles}
                    disabled={releaseFilesLoading}
                    variant="outline"
                  >
                    {releaseFilesLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Search className="w-4 h-4" />
                    )}
                  </Button>
                </div>

                {releaseFilesError && (
                  <p className="text-sm text-red-600">{releaseFilesError}</p>
                )}

                {releaseFilesResult.length > 0 && (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {releaseFilesResult.map((file, idx) => {
                      const fileType = getFileType(file.fileName);
                      const isM1n = fileType === 'M1N';
                      const isDashcam = fileType === 'Dashcam';
                      const isSelectedM1n = newRelease.m1n_upgrade_file?.fileId === file.fileId;
                      const isSelectedDashcam = newRelease.dashcam_upgrade_file?.fileId === file.fileId;
                      
                      return (
                        <div 
                          key={file.fileId || idx}
                          className={`p-2 border rounded text-sm flex items-center justify-between ${
                            isSelectedM1n || isSelectedDashcam ? 'bg-teal-100 border-teal-300' : 'hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <HardDrive className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <span className="truncate">{file.fileName}</span>
                            {isM1n && <Badge className="bg-blue-100 text-blue-700 text-xs">M1N</Badge>}
                            {isDashcam && <Badge className="bg-purple-100 text-purple-700 text-xs">Dashcam</Badge>}
                          </div>
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant={isSelectedM1n ? "default" : "outline"}
                              onClick={() => setNewRelease(prev => ({
                                ...prev,
                                m1n_upgrade_file: isSelectedM1n ? null : file
                              }))}
                              className="text-xs h-7"
                            >
                              <Cpu className="w-3 h-3 mr-1" />
                              M1N
                            </Button>
                            <Button
                              size="sm"
                              variant={isSelectedDashcam ? "default" : "outline"}
                              onClick={() => setNewRelease(prev => ({
                                ...prev,
                                dashcam_upgrade_file: isSelectedDashcam ? null : file
                              }))}
                              className="text-xs h-7"
                            >
                              <Camera className="w-3 h-3 mr-1" />
                              Dashcam
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Selected Files Summary */}
              <div className="grid grid-cols-2 gap-3">
                <div className={`p-3 rounded border ${newRelease.m1n_upgrade_file ? 'bg-blue-50 border-blue-200' : 'bg-gray-50'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <Cpu className="w-4 h-4 text-blue-500" />
                    <span className="font-medium text-sm">M1N File</span>
                  </div>
                  {newRelease.m1n_upgrade_file ? (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-600 truncate">{newRelease.m1n_upgrade_file.fileName}</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setNewRelease(prev => ({ ...prev, m1n_upgrade_file: null }))}
                        className="h-6 w-6 p-0"
                      >
                        <XCircle className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  ) : (
                    <span className="text-xs text-gray-400">Not selected</span>
                  )}
                </div>
                <div className={`p-3 rounded border ${newRelease.dashcam_upgrade_file ? 'bg-purple-50 border-purple-200' : 'bg-gray-50'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <Camera className="w-4 h-4 text-purple-500" />
                    <span className="font-medium text-sm">Dashcam File</span>
                  </div>
                  {newRelease.dashcam_upgrade_file ? (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-600 truncate">{newRelease.dashcam_upgrade_file.fileName}</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setNewRelease(prev => ({ ...prev, dashcam_upgrade_file: null }))}
                        className="h-6 w-6 p-0"
                      >
                        <XCircle className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  ) : (
                    <span className="text-xs text-gray-400">Not selected</span>
                  )}
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label>Notes (optional)</Label>
                <Input
                  placeholder="Release notes..."
                  value={newRelease.notes}
                  onChange={(e) => setNewRelease(prev => ({ ...prev, notes: e.target.value }))}
                />
              </div>

              {/* Create Button */}
              <Button
                onClick={handleCreateRelease}
                disabled={savingRelease || !newRelease.lta_version.trim()}
                className="w-full bg-teal-600 hover:bg-teal-700"
              >
                {savingRelease ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Plus className="w-4 h-4 mr-2" />
                )}
                Create Release
              </Button>
            </div>
          )}

          {/* Release Versions List */}
          {releaseVersionsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
              <span className="ml-2 text-gray-500">Loading releases...</span>
            </div>
          ) : releaseVersions.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Tag className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No release versions registered</p>
              <p className="text-sm">Click "New Release" to add one</p>
            </div>
          ) : (
            <div className="space-y-3">
              {releaseVersions.map((version) => (
                <div 
                  key={version.name}
                  className={`p-4 border rounded-lg bg-white hover:shadow-sm transition-shadow ${
                    version.is_current ? 'border-green-400 border-2 shadow-md' : ''
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="space-y-2 flex-1">
                      <div className="flex items-center gap-2">
                        <Tag className="w-5 h-5 text-teal-600" />
                        <span className="font-semibold text-lg">{version.name}</span>
                        {version.is_current && (
                          <Badge className="bg-green-100 text-green-800 border-green-300">Current</Badge>
                        )}
                        {version.lta_version && version.lta_version !== version.name && (
                          <Badge variant="outline">LTA: {version.lta_version}</Badge>
                        )}
                      </div>
                      
                      {/* Upgrade Files */}
                      <div className="grid grid-cols-2 gap-3 mt-3">
                        <div className={`p-2 rounded border text-sm ${version.m1n_upgrade_file ? 'bg-blue-50 border-blue-200' : 'bg-gray-50'}`}>
                          <div className="flex items-center gap-1 text-blue-700 font-medium">
                            <Cpu className="w-4 h-4" />
                            M1N
                          </div>
                          {version.m1n_upgrade_file ? (
                            <p className="text-xs text-gray-600 truncate mt-1" title={version.m1n_upgrade_file.fileName}>
                              {version.m1n_upgrade_file.fileName}
                            </p>
                          ) : (
                            <p className="text-xs text-gray-400 mt-1">Not configured</p>
                          )}
                        </div>
                        <div className={`p-2 rounded border text-sm ${version.dashcam_upgrade_file ? 'bg-purple-50 border-purple-200' : 'bg-gray-50'}`}>
                          <div className="flex items-center gap-1 text-purple-700 font-medium">
                            <Camera className="w-4 h-4" />
                            Dashcam
                          </div>
                          {version.dashcam_upgrade_file ? (
                            <p className="text-xs text-gray-600 truncate mt-1" title={version.dashcam_upgrade_file.fileName}>
                              {version.dashcam_upgrade_file.fileName}
                            </p>
                          ) : (
                            <p className="text-xs text-gray-400 mt-1">Not configured</p>
                          )}
                        </div>
                      </div>

                      {version.notes && (
                        <p className="text-sm text-gray-500 mt-2">{version.notes}</p>
                      )}

                      {version.created_at && (
                        <p className="text-xs text-gray-400">
                          Created: {new Date(version.created_at).toLocaleString()}
                        </p>
                      )}
                    </div>
                    
                    <div className="flex gap-1">
                      {isAdmin() && !version.is_current && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSetCurrentRelease(version.name)}
                          disabled={savingRelease}
                          className="h-8 text-xs"
                          title="Mark as current release"
                        >
                          {savingRelease ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />}
                        </Button>
                      )}
                      {isAdmin() && (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditingRelease(editingRelease === version.name ? null : version.name)}
                            className="h-8 w-8 p-0"
                          >
                            <Edit className="w-4 h-4 text-gray-500" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteRelease(version.name)}
                            className="h-8 w-8 p-0"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default ReleaseVersionsManager;
